/**
 * scoreComputation
 *
 * Pure, client-side computation of a juror's scores from a case's
 * `scoreDefinitions` / `scoringRules` + the juror's own `demographics`.
 *
 * Scores are never computed or stored server-side — see the "Demographics:
 * Scoring + Predefined Options" plan. This module is intentionally the only
 * place that implements the scoring algorithm on web; iOS implements the
 * identical rule independently (`Utilities/ScoreComputation.swift`) and both
 * are tested against the same worked examples to guard against divergence.
 *
 * Algorithm (per score definition):
 *  - A category is "rule-bearing" for a score if at least one row in
 *    `scoringRules` references that `categoryName` + that score's id.
 *  - If the juror has a `demographics` entry for a rule-bearing category
 *    (regardless of whether its value is an empty string), look for a rule
 *    matching `(categoryName, value)`. Found -> matchType 'value'. Not found
 *    -> matchType 'unmatched', delta 0 (never an error — an off-list legacy
 *    value or simply a value nobody wrote a rule for is a silent no-op).
 *  - If the juror has NO entry at all for a rule-bearing category, look for
 *    that category's "no answer" rule: a row with `optionValue: null`. Found
 *    -> matchType 'noAnswer' with that rule's delta. Not found -> matchType
 *    'noAnswer' with delta 0 (today's behavior when no one bothered to
 *    configure a "no answer" delta).
 *  - `total` is the sum of all contribution deltas.
 *  - `answeredCount` / `ruleCategoryCount` power the "X of Y categories
 *    answered" completeness indicator: answeredCount counts rule-bearing
 *    categories where the juror has an entry; ruleCategoryCount is the total
 *    number of rule-bearing categories for that score.
 *
 * Never throws — always returns an array (empty if inputs are missing/malformed).
 */

/**
 * @typedef {Object} ScoreContribution
 * @property {string} categoryName
 * @property {string|null} value - The juror's value for this category, or null when unanswered.
 * @property {number} delta
 * @property {'value'|'noAnswer'|'unmatched'} matchType
 */

/**
 * @typedef {Object} ComputedScore
 * @property {string} scoreDefinitionId
 * @property {string} name
 * @property {boolean} showOnJurorCard
 * @property {number} total
 * @property {number} answeredCount
 * @property {number} ruleCategoryCount
 * @property {ScoreContribution[]} contributions
 */

/**
 * Compute every score for a single juror on a case.
 * @param {Object} caseObj - Case record (must have scoreDefinitions/scoringRules, both optional/absent-safe).
 * @param {Object} juror - Juror record (must have `demographics`, optional/absent-safe).
 * @returns {ComputedScore[]}
 */
export function computeJurorScores(caseObj, juror) {
  try {
    const scoreDefinitions = Array.isArray(caseObj?.scoreDefinitions) ? caseObj.scoreDefinitions : [];
    const scoringRules = Array.isArray(caseObj?.scoringRules) ? caseObj.scoringRules : [];
    const demographics = Array.isArray(juror?.demographics) ? juror.demographics : [];

    // Build a quick lookup: categoryName -> demographics entry (last one wins,
    // mirroring how the rest of the app treats `demographics` as a small list).
    const demographicsByCategory = new Map();
    for (const entry of demographics) {
      if (entry && typeof entry.name === 'string') {
        demographicsByCategory.set(entry.name, entry);
      }
    }

    return scoreDefinitions.map((scoreDefinition) => {
      const scoreDefinitionId = scoreDefinition?._id;
      const rulesForScore = scoringRules.filter(
        (rule) => rule && rule.scoreDefinitionId === scoreDefinitionId,
      );

      // Rule-bearing categories: distinct categoryName values referenced by
      // this score's rules, in first-seen order.
      const categoryNames = [];
      const seenCategories = new Set();
      for (const rule of rulesForScore) {
        if (rule.categoryName && !seenCategories.has(rule.categoryName)) {
          seenCategories.add(rule.categoryName);
          categoryNames.push(rule.categoryName);
        }
      }

      let total = 0;
      let answeredCount = 0;
      const contributions = categoryNames.map((categoryName) => {
        const demographicEntry = demographicsByCategory.get(categoryName);
        const hasEntry = demographicEntry != null;

        if (hasEntry) {
          answeredCount++;
          const value = demographicEntry.value ?? '';
          const matchingRule = rulesForScore.find(
            (rule) => rule.categoryName === categoryName && rule.optionValue === value,
          );
          const delta = matchingRule ? (Number(matchingRule.delta) || 0) : 0;
          total += delta;
          return {
            categoryName,
            value,
            delta,
            matchType: matchingRule ? 'value' : 'unmatched',
          };
        }

        // No demographics entry at all for this category — look for the
        // category's "no answer" rule (optionValue === null).
        const noAnswerRule = rulesForScore.find(
          (rule) => rule.categoryName === categoryName && rule.optionValue === null,
        );
        const delta = noAnswerRule ? (Number(noAnswerRule.delta) || 0) : 0;
        total += delta;
        return {
          categoryName,
          value: null,
          delta,
          matchType: 'noAnswer',
        };
      });

      return {
        scoreDefinitionId,
        name: scoreDefinition?.name || '',
        // Absent/undefined defaults to true so legacy/existing cases with no
        // explicit setting keep showing every score, matching pre-feature behavior.
        showOnJurorCard: scoreDefinition?.showOnJurorCard !== false,
        total,
        answeredCount,
        ruleCategoryCount: categoryNames.length,
        contributions,
      };
    });
  } catch (error) {
    console.error('computeJurorScores failed, returning no scores:', error);
    return [];
  }
}

export default { computeJurorScores };
